import { useEffect } from 'react';
import { Select, Typography } from 'antd';
import { Spin } from 'antd';
import { useAppDispatch } from 'Redux/store';
import { useSelector } from 'react-redux';
import {
  selectDriverList,
  selectCurrentDriver,
  selectDriverLoading,
  selectDriverError,
  resetDrivers,
} from 'Redux/Driver/slice';
import { selectUser } from 'Redux/User/slice';
import { selectCurrentOrder } from 'Redux/Order/slice';
import { selectMilesOut } from 'Redux/Location/slice';
import { selectUserLoading } from 'Redux/User/slice';
import { selectLocationLoading } from 'Redux/Location/slice';
import { getDriverList, getDriver } from 'Redux/Driver/reducer';
import { getUser } from 'Redux/User/reducer';
import { getMilesOut } from 'Redux/Location/reducer';
import { resetCurrentMilesOut } from 'Redux/Location/slice';
import { setUserToNull } from 'Redux/User/slice';
import ContentWithBreaks from 'Components/UI/ContentWithBreaks';
import { dimsToString, formatDate } from 'Shared/Utils';
import styles from './Driver.module.scss';

const { Option } = Select;

export const Driver = () => {
  const driverList = useSelector(selectDriverList);
  const driver = useSelector(selectCurrentDriver);
  const currentOrder = useSelector(selectCurrentOrder);
  const milesOut = useSelector(selectMilesOut);
  const dispatcher = useSelector(selectUser);
  const locationLoading = useSelector(selectLocationLoading);
  const driverLoading = useSelector(selectDriverLoading);
  const driverLoadingError = useSelector(selectDriverError);
  const userLoading = useSelector(selectUserLoading);
  const dispatch = useAppDispatch();

  const handleSelectChange = (value: number) => {
    dispatch(getDriver(value));
  };

  useEffect(() => {
    dispatch(getDriverList());

    return () => {
      dispatch(resetDrivers());
      dispatch(resetCurrentMilesOut());
      dispatch(setUserToNull());
    };
  }, [dispatch]);

  useEffect(() => {
    if (driver) {
      dispatch(getUser(driver?.vehicle.dispatcher));

      dispatch(
        getMilesOut({
          coordinateFrom: driver?.vehicle.coordinate_from,
          coordinateTo: currentOrder?.coordinate_from || '',
        }),
      );
    }
  }, [driver, currentOrder, dispatch]);

  return (
    <div className={styles.driver}>
      <div className={styles.driver__row}>
        <span className={styles.driver__selectTitle}>DRIVER</span>
        <Select
          placeholder={
            driverLoading
              ? 'Driver(s) is loading...'
              : driverLoadingError
              ? 'Driver(s) loading failed'
              : driverList?.length
              ? 'Select a driver...'
              : 'No drivers to select'
          }
          onChange={handleSelectChange}
          showSearch
          className={styles.driver__select}
          variant="borderless"
          disabled={!driverList || !driverList.length}
        >
          {driverList?.map((driver) => (
            <Option key={driver.id} value={driver.id}>
              {`${driver.first_name} ${driver.last_name}`}
            </Option>
          ))}
        </Select>
      </div>
      <div className={styles.driver__row}>
        <span className={styles.driver__title}>MILES OUT</span>
        <span className={styles.driver__value}>
          {milesOut
            ? milesOut.distanceInMi
              ? `${milesOut.distanceInMi} mi`
              : ''
            : !!driver && locationLoading && <Spin />}
        </span>
      </div>
      <div className={styles.driver__row}>
        <span className={styles.driver__title}>VEHICLE</span>
        <div className={styles.driver__value}>
          <span className={styles.driver__value__item}>
            {driver?.vehicle.transport_type}
          </span>
          <span className={styles.driver__value__item_blue}>
            {dimsToString({
              length: driver?.vehicle.length,
              width: driver?.vehicle.width,
              height: driver?.vehicle.height,
            })}
          </span>
        </div>
      </div>
      <div className={styles.driver__row}>
        <span className={styles.driver__title}>PAYLOAD</span>
        <div className={styles.driver__value}>
          <span className={styles.driver__value__item}>
            {driver?.vehicle.payload && `${driver?.vehicle.payload} lbs`}
          </span>
          <span className={styles.driver__value__item_red}>
            {driver?.vehicle?.location_to && (
              <ContentWithBreaks
                content={'Going to '.concat(driver.vehicle.location_to)}
                separator="\n"
              />
            )}
          </span>
        </div>
      </div>
      <div className={styles.driver__row}>
        <span className={styles.driver__title}>LOCATION</span>
        <div className={styles.driver__value}>
          <span className={styles.driver__value__item}>
            <ContentWithBreaks
              content={driver?.vehicle.location_from}
              separator="\n"
            />
          </span>
          <span className={styles.driver__value__item_red}>
            {formatDate({
              date: driver?.vehicle.location_from_date,
              outputFormat: 'TIME_date',
            })}
          </span>
        </div>
      </div>
      <div className={styles.driver__row}>
        <span className={styles.driver__title}>BID</span>
      </div>
      <div className={styles.driver__row}>
        <span className={styles.driver__title}>RESPONSIBLE</span>
        <div className={styles.driver__value}>
          {driver && dispatcher?.first_name && dispatcher?.last_name
            ? `${dispatcher?.first_name} ${dispatcher?.last_name}`
            : driver && userLoading && <Spin />}
        </div>
      </div>
      <div className={styles.driver__row}>
        <span className={styles.driver__title}>CALL</span>
        <div className={styles.driver__value}>
          <span className={styles.driver__value__item}>
            <Typography.Link
              className={styles.driver__value__link}
              href={`tel:${driver?.driver.phone_number}`}
              target="_blank"
              rel="noreferrer"
            >
              {driver?.driver.phone_number}
            </Typography.Link>
          </span>
        </div>
      </div>
    </div>
  );
};
